import './load-reference-sketch-modal.scss';
import type { IFrontendServiceContainer } from '@phi/frontend/service-container';
import type { Option } from '@vaaas/fpts/option';
import { type PropType, computed, defineComponent, h, reactive, watch } from 'vue';
import { load_local_sketch, load_sketch } from '@frontend/reference_sketches';
import FormButtonSelect from '@frontend/components/FormButtonSelect';
import { FormNumber } from '@components/form';
import LoadedSketchElement from '@frontend/LoadedSketchElement';
import { Dialog, DragAndDropFile, utility } from '@phi/components';
import { vif1 } from '@phi/util/vuetils';
import { OnshapeSketchValue, SelectReferenceSketchFromOnshape } from './select-reference-sketch-from-onshape';
import Tabs from '@frontend/components/Tabs';

type Orientation = 'xz' | 'yz' | 'xy';

const OrientationSelect = FormButtonSelect<Orientation>();

export const LoadReferenceSketchModal = defineComponent({
    props: {
        show: { required: true, type: Boolean },
        app: { required: true, type: Object as PropType<IFrontendServiceContainer> }
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    setup(props, { emit }) {
        const accepted_image_types =
            ['png', 'jpg', 'jpeg']
                .flatMap(x => [x, x.toUpperCase()])
                .map(x => '.' + x)
                .join(', ');

        const app = props.app;

        type TabType = 'local' | 'onshape';

        const data = reactive({
            orientation: 'xz' as Orientation,
            height: undefined as undefined | number,
            width: undefined as undefined | number,
            onshape_sketch_value: {
                document: app.state.document,
                workspace: app.state.workspace,
                element: undefined,
            } as OnshapeSketchValue,
            local_file: undefined as Option<File>,
            tab: (
                props.app.authentication.session_type.value.has('onshape')
                    ? 'onshape'
                    : 'local'
            ) as TabType,
        });

        function has_selected_something() {
            return data.local_file !== undefined
                || data.onshape_sketch_value.element !== undefined;
        }

        function reset() {
            data.orientation = 'xz';
            data.height = undefined;
            data.width = undefined;
            data.onshape_sketch_value = {
                element: undefined,
                document: app.state.document,
                workspace: app.state.workspace,
            };
            data.tab = app.authentication.session_type.value.has('onshape') ? 'onshape' : 'local';
            data.local_file = undefined;
        }

        const loaded_sketch_element = computed(() => {
            if (!data.onshape_sketch_value.element)
                return undefined;
            const sketch = new LoadedSketchElement();
            sketch.documentId = app.state.document;
            sketch.workspaceId = app.state.workspace;
            sketch.elementId = data.onshape_sketch_value.element.id;
            sketch.versionId = data.onshape_sketch_value.element.microversionid;
            sketch.filename = data.onshape_sketch_value.element.name;
            return sketch;
        });

        function on_load() {
            if (data.local_file) {
                load_local_sketch(
                    data.local_file,
                    data.width ?? 0,
                    data.height ?? 0,
                    data.orientation,
                );
                emit('update:show', false);
            } else if (loaded_sketch_element.value) {
                app.state.document = data.onshape_sketch_value.document;
                app.state.workspace = data.onshape_sketch_value.workspace;
                load_sketch(
                    loaded_sketch_element.value,
                    data.width,
                    data.height,
                    data.orientation,
                );
                emit('update:show', false);
            }
        }

        watch(
            () => props.show,
            (value) => {
                if (value) reset();
            },
        );

        const tabs = computed(() => {
            const tabs = [];
            if (app.authentication.session_type.value.has('onshape'))
                tabs.push({
                    id: 'onshape',
                    text: 'Onshape',
                    slot: () => h(SelectReferenceSketchFromOnshape, {
                        value: data.onshape_sketch_value,
                        'onUpdate:value': x => data.onshape_sketch_value = x,
                    }),
                });
            tabs.push({
                id: 'local',
                text: 'Local file',
                slot: () => h(DragAndDropFile, {
                    value: data.local_file,
                    'onUpdate:value': x => data.local_file = x,
                    type: accepted_image_types,
                }),
            });
            return tabs;
        });

        return () => h(Dialog, {
            show: props.show,
            'onUpdate:show': () => emit('update:show', false),
            id: 'sketchImagesModal',
            title: 'Load sketch image',
            confirm_label: 'Load',
            onConfirm: on_load,
            onClose: () => emit('update:show', false),
        }, () => h('div', { class: utility.margin_gap_5 }, [
            vif1(props.show)(() => h(Tabs, {
                tab: data.tab,
                'onUpdate:tab': x => data.tab = x as TabType,
                tabs: tabs.value,
            })),

            h('div', [
                h('h5', 'Image orientation'),

                h(OrientationSelect, {
                    options: [
                        {
                            value: 'xz',
                            label: 'Front',
                        },
                        {
                            value: 'yz',
                            label: 'Side',
                        },
                        {
                            value: 'xy',
                            label: 'Top',
                        },
                    ],
                    value: data.orientation,
                    'onUpdate:value': x => data.orientation = x,
                }),
            ]),

            h('div', [
                h('h5', 'Select image size'),

                h('p', 'Note: If you leave width or height blank, the current image ratio will be preserved.'),

                h('div', { class: 'dimensions' }, [
                    h('div', [
                        h('label', { for: 'sketch-images-width' }, 'Width'),
                        h(FormNumber, {
                            id: 'sketch-images-width',
                            value: data.width,
                            'onUpdate:value': x => data.width = x,
                        }),
                    ]),

                    h('div', [
                        h('label', { for: 'sketch-images-height' }, 'Height'),
                        h(FormNumber, {
                            id: 'sketch-images-height',
                            value: data.height,
                            'onUpdate:value': x => data.height = x,
                        }),
                    ]),
                ]),
            ]),
        ]));
    },
});
